package one.healthium.cali.presentation.feature.today.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.LifecycleResumeEffect
import ifaandroid.apps.cali.composeapp.generated.resources.Res
import ifaandroid.apps.cali.composeapp.generated.resources.ic_burn_24
import ifaandroid.apps.cali.composeapp.generated.resources.ic_info_9
import ifaandroid.apps.cali.composeapp.generated.resources.ic_steps_24
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.format.char
import one.healthium.base.utils.localizedDayOfWeekNames
import one.healthium.base.utils.localizedMonthNames
import loco.CALI_TODAY_CALORIES_DAILY_PROGRESS_INFO_LABEL
import loco.CALI_TODAY_CALORIES_DAILY_PROGRESS_INFO_TEXT
import loco.CALI_TODAY_OVERALL_DAILY_PROGRESS_HEADER
import loco.CALI_TODAY_OVERALL_DAILY_PROGRESS_INFO_TEXT
import loco.CALI_TODAY_STEPS_DAILY_PROGRESS_INFO_LABEL
import loco.CALI_TODAY_STEPS_DAILY_PROGRESS_INFO_TEXT
import loco.GENERAL_OK
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.base.utils.toLocalDate
import one.healthium.cali.paytone_16
import one.healthium.cali.paytone_18
import one.healthium.cali.paytone_28
import one.healthium.loco.resolve
import one.healthium.today.ui.calories_counter.CaloriesCounterUiModel
import one.healthium.today.ui.models.DailyStepsUiModel
import one.healthium.ui.compose.components.AppAlertDialog
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black
import one.healthium.ui.compose.theme.black_6
import one.healthium.ui.compose.theme.black_7
import one.healthium.ui.compose.theme.gray_16_20
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes

@Composable
fun TodayDailyProgress(
    modifier: Modifier = Modifier,
    todayCaloriesCounter: CaloriesCounterUiModel?,
    todayStepsCounter: DailyStepsUiModel?,
    overallProgress: Float,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp)
    ) {
        TodaySection()
        DailyProgress(
            todayCaloriesCounter = todayCaloriesCounter,
            todayStepsCounter = todayStepsCounter,
            overallProgress = overallProgress,
        )
    }
}

@Composable
private fun TodaySection() {
    var now by remember { mutableStateOf(Clock.System.now().toLocalDate()) }
    val scope = rememberCoroutineScope()

    LifecycleResumeEffect(Unit) {
        val job = scope.launch {
            while (isActive) {
                now = Clock.System.now().toLocalDate()
                delay(1.minutes.inWholeMilliseconds)
            }
        }
        onPauseOrDispose {
            job.cancel()
        }
    }
    val nowDate = TodayDateFormat().format(now)

    Text(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 24.dp, bottom = 12.dp),
        text = nowDate,
        style = AppTheme.textStyles.paytone_18,
        color = AppTheme.colors.text300,
        maxLines = 1,
    )
}

@Composable
private fun DailyProgress(
    todayCaloriesCounter: CaloriesCounterUiModel?,
    todayStepsCounter: DailyStepsUiModel?,
    overallProgress: Float,
) {
    Row(
        modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
        verticalAlignment = Alignment.Bottom,
    ) {
        OverallProgress(
            modifier = Modifier.weight(1f).fillMaxHeight(),
            overallProgress = overallProgress,
        )
        CaloriesProgress(
            modifier = Modifier.weight(1f),
            todayCaloriesCounter = todayCaloriesCounter,
            todayStepsCounter = todayStepsCounter,
        )
    }
}

@Composable
private fun OverallProgress(
    modifier: Modifier = Modifier,
    overallProgress: Float,
) {
    Column(
        modifier = modifier
            .background(
                brush = Brush.verticalGradient(colors = listOf(black_6, black_7)),
                shape = RoundedCornerShape(topStart = 24.dp, topEnd = 24.dp, bottomStart = 24.dp)
            )
            .padding(horizontal = 16.dp, vertical = 24.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Text(
            text = stringResource(loco.Res.string.CALI_TODAY_OVERALL_DAILY_PROGRESS_HEADER),
            style = AppTheme.textStyles.paytone_16,
            color = AppTheme.colors.textInverted100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(14.dp))
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center,
        ) {
            val animatedProgress by animateFloatAsState(
                targetValue = overallProgress,
                animationSpec = tween(durationMillis = 300, delayMillis = 200),
            )

            CircularProgressIndicator(
                modifier = Modifier.size(135.dp),
                color = AppTheme.colors.primary,
                trackColor = gray_16_20,
                strokeWidth = 17.dp,
                strokeCap = StrokeCap.Round,
                progress = { animatedProgress },
            )
            Text(
                modifier = Modifier.testTag("overall_progress"),
                text = "${(overallProgress * 100).toInt()}%",
                style = AppTheme.textStyles.paytone_28,
                color = AppTheme.colors.textInverted100,
            )
        }
        Spacer(Modifier.height(10.dp))
        InfoRow(
            name = stringResource(loco.Res.string.CALI_TODAY_OVERALL_DAILY_PROGRESS_INFO_TEXT),
            infoText = stringResource(loco.Res.string.CALI_TODAY_OVERALL_DAILY_PROGRESS_INFO_TEXT),
            tint = white,
            infoIconTint = black,
        )
    }
}

@Composable
private fun CaloriesProgress(
    modifier: Modifier = Modifier,
    todayCaloriesCounter: CaloriesCounterUiModel?,
    todayStepsCounter: DailyStepsUiModel?,
) {
    Column(
        modifier = modifier
            .padding(top = 34.dp)
            .background(
                color = AppTheme.colors.primary,
                shape = RoundedCornerShape(topEnd = 24.dp, bottomEnd = 24.dp)
            )
            .padding(horizontal = 16.dp, vertical = 24.dp),
        horizontalAlignment = Alignment.Start,
        verticalArrangement = Arrangement.Center,
    ) {
        InfoPane(
            icon = Res.drawable.ic_burn_24,
            name = stringResource(loco.Res.string.CALI_TODAY_CALORIES_DAILY_PROGRESS_INFO_LABEL),
            value = todayCaloriesCounter?.caloriesGoal?.amount?.formatCalories(addUnit = false)?.resolve(),
            infoText = stringResource(loco.Res.string.CALI_TODAY_CALORIES_DAILY_PROGRESS_INFO_TEXT),
        )
        Spacer(Modifier.height(12.dp))
        InfoPane(
            icon = Res.drawable.ic_steps_24,
            name = stringResource(loco.Res.string.CALI_TODAY_STEPS_DAILY_PROGRESS_INFO_LABEL),
            value = todayStepsCounter?.steps?.toString(),
            infoText = stringResource(loco.Res.string.CALI_TODAY_STEPS_DAILY_PROGRESS_INFO_TEXT),
        )
    }
}

@Composable
private fun InfoPane(
    icon: DrawableResource,
    name: String,
    value: String?,
    infoText: String,
) {
    Image(
        painter = painterResource(icon),
        contentDescription = null,
    )
    Spacer(Modifier.height(4.dp))
    Text(
        text = value ?: "—",
        style = AppTheme.textStyles.paytone_28,
        color = AppTheme.colors.onPrimary,
    )
    InfoRow(
        modifier = Modifier.offset(y = -(6.dp)),
        name = name,
        infoText = infoText,
        tint = black,
    )
}

@Composable
private fun InfoRow(
    modifier: Modifier = Modifier,
    name: String,
    infoText: String,
    tint: Color,
    infoIconTint: Color = AppTheme.colors.primary,
) {
    var showInfoAlert by remember { mutableStateOf(false) }

    if (showInfoAlert) {
        AppAlertDialog(
            onDismissRequest = { showInfoAlert = false },
            title = name,
            text = infoText,
            positiveButton = stringResource(loco.Res.string.GENERAL_OK),
            onPositiveClick = {
                showInfoAlert = false
            },
        )
    }

    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = name,
            style = AppTheme.textStyles.secondary_9_400,
            color = tint.copy(alpha = 0.5f),
        )
        Surface(
            modifier = Modifier.size(21.dp).padding(6.dp),
            shape = RoundedCornerShape(100),
            color = Color.Transparent,
            onClick = {
                showInfoAlert = !showInfoAlert
            },
        ) {
            Icon(
                modifier = Modifier
                    .fillMaxSize()
                    .background(
                        color = tint,
                        shape = RoundedCornerShape(100)
                    ),
                painter = painterResource(Res.drawable.ic_info_9),
                contentDescription = null,
                tint = infoIconTint,
            )
        }
    }
}

private fun TodayDateFormat() = LocalDate.Format {
    dayOfWeek(localizedDayOfWeekNames(abbreviated = true))
    char(',')
    char(' ')
    dayOfMonth()
    char(' ')
    monthName(localizedMonthNames(abbreviated = true))
    char(' ')
    year()
}